/**
 * MaoDeObraDominio — seção "Mão de obra & Competências" da Config (D8).
 *
 * Cadastro PROVISÓRIO de executores (OperadorPin com campos RH aditivos):
 * quando o módulo RH nascer, ele assume como fonte — a interface (executor
 * + competências + disponibilidade) não muda (contrato D8 do brief).
 *
 * Tabela CRUD (/api/operadores) + SlideOver form (função, nível, flag
 * executor com hint do board, competências F5+F6, admissão, PIN write-only)
 * + actions resetar-pin/desbloquear + bloco de indisponibilidades.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FiX } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import UserAvatar from "../../../components/UserAvatar";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarOperador,
  criarOperador,
  desbloquearOperador,
  listarOperadores,
  resetarPinOperador,
} from "../../../services/producao";
import {
  CATALOGO_ETAPAS_INFO,
  FUNCOES_OPERADOR,
  MOTIVOS_INDISPONIBILIDADE,
  NIVEIS_OPERADOR,
  NIVEL_OPERADOR_TONES,
  criarIndisponibilidadeOperador,
  listarIndisponibilidades,
  removerIndisponibilidadeOperador,
} from "../../../services/producaoV2";
import { unidadeDefault } from "../../../services/unidades";
import { formatarDataCurta } from "../../../utils/dataHora";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  InfoMsg,
  Pill,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
} from "../../Orcamentos/styles";
import { Mono, NotaDiscreta, TabelaFixa, TituloBloco } from "../_shared/ui";
import {
  ChipToggle,
  FieldControleCol,
  FieldLabelCol,
  FieldRowBox,
  InputConfig,
  LinhaChips,
  LinhaEditor,
  LinkDiscreto,
  ListaEditor,
  SelectConfig,
  ToggleSwitch,
} from "./ui";

const ETAPAS_COMPETENCIA = CATALOGO_ETAPAS_INFO.filter(
  (e) => e.fase === "INDUSTRIALIZACAO" || e.fase === "MARCENARIA",
);

const ROTULO_ETAPA = Object.fromEntries(
  CATALOGO_ETAPAS_INFO.map((e) => [e.codigo, e.label]),
);

// ---------------------------------------------------------------------------
// Bloco de indisponibilidades (dentro do form de edição)
// ---------------------------------------------------------------------------

function BlocoIndisponibilidades({ operadorId }) {
  const { request } = useAuth();
  const [janelas, setJanelas] = useState(null);
  const [erro, setErro] = useState(null);
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [motivo, setMotivo] = useState("");

  const carregar = useCallback(() => {
    listarIndisponibilidades(request, operadorId)
      .then((r) => setJanelas(r?.indisponibilidades || []))
      .catch((err) =>
        setErro(err?.message || "Não foi possível carregar as janelas."),
      );
  }, [request, operadorId]);

  useEffect(() => {
    setJanelas(null);
    setErro(null);
    setInicio("");
    setFim("");
    setMotivo("");
    carregar();
  }, [carregar]);

  return (
    <div style={{ marginTop: 18 }}>
      <TituloBloco>Indisponibilidades (férias / falta / afastamento)</TituloBloco>
      {erro && <ErrorMsg style={{ marginBottom: 8 }}>{erro}</ErrorMsg>}
      {janelas === null && !erro && <EmptyMsg>Carregando…</EmptyMsg>}
      {janelas !== null && janelas.length === 0 && (
        <NotaDiscreta style={{ margin: "0 0 8px" }}>
          Nenhuma janela registrada — o colaborador conta na capacidade do
          board em todos os dias úteis.
        </NotaDiscreta>
      )}
      <ListaEditor>
        {(janelas || []).map((j) => (
          <LinhaEditor key={j.id}>
            <span className="rotulo">
              <Mono>
                {formatarDataCurta(j.inicio)} – {formatarDataCurta(j.fim)}
              </Mono>{" "}
              · {j.motivo_display || j.motivo}
            </span>
            <AsyncButton
              variant="icon"
              silentError
              silentSuccess
              onClick={async () => {
                await removerIndisponibilidadeOperador(request, operadorId, j.id);
                carregar();
              }}
              title="Remover janela"
            >
              <FiX size={14} aria-hidden="true" />
            </AsyncButton>
          </LinhaEditor>
        ))}
        <LinhaEditor>
          <InputConfig
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            aria-label="Início da indisponibilidade"
          />
          <span>–</span>
          <InputConfig
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            aria-label="Fim da indisponibilidade"
          />
          <SelectConfig
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            style={{ maxWidth: 160 }}
            aria-label="Motivo"
          >
            <option value="">Motivo…</option>
            {MOTIVOS_INDISPONIBILIDADE.map((m) => (
              <option key={m.codigo} value={m.codigo}>
                {m.label}
              </option>
            ))}
          </SelectConfig>
          <AsyncButton
            variant="secondary"
            silentError
            disabled={!inicio || !fim || !motivo}
            successMessage="Indisponibilidade registrada."
            onClick={async () => {
              await criarIndisponibilidadeOperador(request, operadorId, {
                inicio,
                fim,
                motivo,
              });
              setInicio("");
              setFim("");
              setMotivo("");
              carregar();
            }}
          >
            Adicionar
          </AsyncButton>
        </LinhaEditor>
      </ListaEditor>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form (criar / editar) — SlideOver
// ---------------------------------------------------------------------------

function FormOperador({ aberto, operador, onFechar, onSalvo }) {
  const { request } = useAuth();
  const [nome, setNome] = useState("");
  const [funcao, setFuncao] = useState("MARCENEIRO");
  const [nivel, setNivel] = useState("");
  const [ehExecutor, setEhExecutor] = useState(true);
  const [ehSupervisor, setEhSupervisor] = useState(false);
  const [competencias, setCompetencias] = useState([]);
  const [admitidoEm, setAdmitidoEm] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [pin, setPin] = useState("");
  // ADR-0077: a mesma pessoa com login no ERP e PIN na fábrica é UMA no chat.
  const [usuarioVinculado, setUsuarioVinculado] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [erro, setErro] = useState(null);
  const [pinNovo, setPinNovo] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setNome(operador?.nome || "");
    setFuncao(operador?.funcao || "MARCENEIRO");
    setNivel(operador?.nivel || "");
    setEhExecutor(operador ? Boolean(operador.eh_executor_producao) : true);
    setEhSupervisor(Boolean(operador?.eh_supervisor));
    setCompetencias(operador?.competencias || []);
    setAdmitidoEm(operador?.admitido_em || "");
    setAtivo(operador ? Boolean(operador.ativo) : true);
    setUsuarioVinculado(operador?.usuario || "");
    setPin("");
    setErro(null);
    setPinNovo(null);
  }, [aberto, operador]);

  useEffect(() => {
    if (!aberto) return;
    import("../../../services/minhasPendencias")
      .then(({ listarUsuariosAtribuiveis }) => listarUsuariosAtribuiveis(request))
      .then((p) => setUsuarios(Array.isArray(p) ? p : p?.results || p?.data || []))
      .catch(() => setUsuarios([]));
  }, [aberto, request]);

  const alternarCompetencia = (codigo) =>
    setCompetencias((atual) =>
      atual.includes(codigo)
        ? atual.filter((c) => c !== codigo)
        : [...atual, codigo],
    );

  const salvar = async () => {
    setErro(null);
    if (!nome.trim()) {
      setErro("Informe o nome do colaborador.");
      throw new Error("validação");
    }
    if (!operador && !/^\d{4,6}$/.test(pin)) {
      setErro("PIN é obrigatório na criação (4 a 6 dígitos).");
      throw new Error("validação");
    }
    if (operador && pin && !/^\d{4,6}$/.test(pin)) {
      setErro("PIN deve ter 4 a 6 dígitos (deixe vazio para manter o atual).");
      throw new Error("validação");
    }
    const dados = {
      nome: nome.trim(),
      funcao,
      nivel,
      eh_executor_producao: ehExecutor,
      eh_supervisor: ehSupervisor,
      competencias,
      admitido_em: admitidoEm || null,
      usuario: usuarioVinculado || null,
      ativo,
    };
    if (pin) dados.pin = pin;
    try {
      if (operador) {
        await atualizarOperador(request, operador.id, dados);
      } else {
        const unidade = await unidadeDefault(request);
        if (!unidade?.id) {
          setErro("Nenhuma unidade cadastrada — cadastre a fábrica primeiro.");
          throw new Error("sem unidade");
        }
        await criarOperador(request, { ...dados, unidade: unidade.id });
      }
      onSalvo();
      onFechar();
    } catch (err) {
      if (!erro) setErro(err?.message || "Não foi possível salvar.");
      throw err;
    }
  };

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title={operador ? `Editar ${operador.nome}` : "Novo colaborador"}
      subtitle="Cadastro provisório — o módulo RH assume como fonte (contrato D8)"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
          <SecondaryButton type="button" onClick={onFechar}>
            Cancelar
          </SecondaryButton>
          <AsyncButton
            silentError
            successMessage={operador ? "Colaborador atualizado." : "Colaborador criado."}
            onClick={salvar}
          >
            Salvar colaborador
          </AsyncButton>
        </div>
      }
    >
      <FieldRowBox>
        <FieldLabelCol>Nome *</FieldLabelCol>
        <FieldControleCol>
          <InputConfig
            type="text"
            value={nome}
            maxLength={120}
            style={{ maxWidth: "100%" }}
            onChange={(e) => setNome(e.target.value)}
          />
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Usuário do ERP</FieldLabelCol>
        <FieldControleCol>
          <SelectConfig value={usuarioVinculado} onChange={(e) => setUsuarioVinculado(e.target.value)}>
            <option value="">Não tem login no ERP</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </SelectConfig>
          {!usuarioVinculado && operador?.usuario_sugerido && (
            <button
              type="button"
              style={{ marginTop: 6, border: "1px solid var(--info-border, var(--border))", background: "var(--info-bg, transparent)", color: "var(--info-text, var(--ink-2))", borderRadius: 999, padding: "4px 10px", font: "inherit", fontSize: 12.5, cursor: "pointer" }}
              onClick={() => setUsuarioVinculado(operador.usuario_sugerido.id)}
            >
              Parece ser {operador.usuario_sugerido.nome} — usar este login
            </button>
          )}
          <small style={{ display: "block", color: "var(--ink-3)", marginTop: 4 }}>
            Se esta pessoa também entra no ERP com login, escolha o usuário: no Chat ela aparece uma vez só, com o mesmo histórico no tablet e no computador.
          </small>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Função</FieldLabelCol>
        <FieldControleCol>
          <SelectConfig value={funcao} onChange={(e) => setFuncao(e.target.value)}>
            {FUNCOES_OPERADOR.map((f) => (
              <option key={f.codigo} value={f.codigo}>
                {f.label}
              </option>
            ))}
          </SelectConfig>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Nível</FieldLabelCol>
        <FieldControleCol>
          <SelectConfig value={nivel} onChange={(e) => setNivel(e.target.value)}>
            <option value="">Não informado</option>
            {NIVEIS_OPERADOR.map((n) => (
              <option key={n.codigo} value={n.codigo}>
                {n.label}
              </option>
            ))}
          </SelectConfig>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Executor de produção</FieldLabelCol>
        <FieldControleCol>
          <ToggleSwitch
            checked={ehExecutor}
            onChange={setEhExecutor}
            rotulo={ehExecutor ? "Sim" : "Não"}
          />
          <NotaDiscreta style={{ margin: 0 }}>
            Quando ligado, aparece como coluna no board do Planejamento
            Marcenaria (recebe alocação de etapas de F6).
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Supervisor</FieldLabelCol>
        <FieldControleCol>
          <ToggleSwitch
            checked={ehSupervisor}
            onChange={setEhSupervisor}
            rotulo={ehSupervisor ? "Sim" : "Não"}
          />
          <NotaDiscreta style={{ margin: 0 }}>
            Habilita o override de roteiro no terminal de bipagem.
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Competências</FieldLabelCol>
        <FieldControleCol>
          <LinhaChips>
            {ETAPAS_COMPETENCIA.map((etapa) => (
              <ChipToggle
                key={etapa.codigo}
                type="button"
                $ativo={competencias.includes(etapa.codigo)}
                aria-pressed={competencias.includes(etapa.codigo)}
                onClick={() => alternarCompetencia(etapa.codigo)}
              >
                {etapa.label}
              </ChipToggle>
            ))}
          </LinhaChips>
          <NotaDiscreta style={{ margin: 0 }}>
            Etapas de F5/F6 que o colaborador sabe executar — alimenta o
            aviso de elegibilidade na alocação (não bloqueia).
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>
      <FieldRowBox>
        <FieldLabelCol>Admitido em</FieldLabelCol>
        <FieldControleCol>
          <InputConfig
            type="date"
            value={admitidoEm || ""}
            onChange={(e) => setAdmitidoEm(e.target.value)}
          />
          <NotaDiscreta style={{ margin: 0 }}>
            Base do "anos de casa" mostrado no board.
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>
      {operador && (
        <FieldRowBox>
          <FieldLabelCol>Ativo</FieldLabelCol>
          <FieldControleCol>
            <ToggleSwitch
              checked={ativo}
              onChange={setAtivo}
              rotulo={ativo ? "Ativo" : "Inativo (soft-delete)"}
            />
          </FieldControleCol>
        </FieldRowBox>
      )}
      <FieldRowBox>
        <FieldLabelCol>{operador ? "Trocar PIN" : "PIN *"}</FieldLabelCol>
        <FieldControleCol>
          <InputConfig
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={6}
            value={pin}
            placeholder={operador ? "vazio = manter atual" : "4–6 dígitos"}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
          />
          <NotaDiscreta style={{ margin: 0 }}>
            PIN do terminal de bipagem — nunca é exibido depois de salvo.
          </NotaDiscreta>
        </FieldControleCol>
      </FieldRowBox>

      {operador && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <AsyncButton
            variant="secondary"
            silentError
            silentSuccess
            onClick={async () => {
              const resposta = await resetarPinOperador(request, operador.id);
              setPinNovo(resposta?.pin_novo || null);
            }}
          >
            Resetar PIN
          </AsyncButton>
          {operador.esta_bloqueado && (
            <AsyncButton
              variant="secondary"
              silentError
              successMessage="Lockout removido."
              onClick={async () => {
                await desbloquearOperador(request, operador.id);
                onSalvo();
              }}
            >
              Desbloquear
            </AsyncButton>
          )}
        </div>
      )}
      {pinNovo && (
        <InfoMsg style={{ marginTop: 10 }}>
          PIN novo: <Mono>{pinNovo}</Mono> — anote agora, não será mostrado
          novamente.
        </InfoMsg>
      )}
      {erro && <ErrorMsg style={{ marginTop: 12 }}>{erro}</ErrorMsg>}

      {operador && <BlocoIndisponibilidades operadorId={operador.id} />}
    </SlideOverPanel>
  );
}

// ---------------------------------------------------------------------------
// Seção
// ---------------------------------------------------------------------------

export default function MaoDeObraDominio() {
  const { request, hasPermission } = useAuth();
  const podeVer = hasPermission("operador:view");
  const podeGerir = hasPermission("operador:manage");

  const [operadores, setOperadores] = useState(null);
  const [erro, setErro] = useState(null);
  const [form, setForm] = useState(null); // null | {operador?}
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = useCallback(async () => {
    if (!podeVer) return;
    setErro(null);
    try {
      const resposta = await listarOperadores(request, {});
      setOperadores(
        Array.isArray(resposta) ? resposta : resposta?.results || [],
      );
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar os colaboradores.");
      setOperadores(null);
    }
  }, [request, podeVer]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const linhas = useMemo(() => {
    const todos = operadores || [];
    return mostrarInativos ? todos : todos.filter((o) => o.ativo);
  }, [operadores, mostrarInativos]);

  if (!podeVer) {
    return (
      <Card>
        <SectionTitle>Mão de obra & Competências</SectionTitle>
        <InfoMsg>
          Esta seção exige a permissão operador:view — fale com o gestor de
          acessos.
        </InfoMsg>
      </Card>
    );
  }

  return (
    <>
      <InfoMsg>
        Cadastro provisório — quando o módulo RH nascer, ele assume como
        fonte (contrato D8). A interface (executores, competências e
        disponibilidade) permanece a mesma.
      </InfoMsg>

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <SectionTitle style={{ margin: 0 }}>
            Colaboradores da produção
          </SectionTitle>
          <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
            <LinkDiscreto
              type="button"
              onClick={() => setMostrarInativos((v) => !v)}
            >
              {mostrarInativos ? "Ocultar inativos" : "Mostrar inativos"}
            </LinkDiscreto>
            {podeGerir && (
              <PrimaryButton type="button" onClick={() => setForm({})}>
                + Novo colaborador
              </PrimaryButton>
            )}
          </span>
        </div>

        {erro && <ErrorMsg style={{ marginTop: 10 }}>{erro}</ErrorMsg>}
        {operadores === null && !erro && <EmptyMsg>Carregando…</EmptyMsg>}
        {Array.isArray(operadores) && linhas.length === 0 && (
          <EmptyMsg>
            Nenhum colaborador cadastrado — crie o primeiro executor para o
            board da Marcenaria existir.
          </EmptyMsg>
        )}

        {linhas.length > 0 && (
          <TabelaFixa style={{ marginTop: 10 }}>
            <colgroup>
              <col style={{ width: "22%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "11%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Função</th>
                <th>Nível</th>
                <th>Executor?</th>
                <th>Competências</th>
                <th>Supervisor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((op) => {
                const comps = (op.competencias || [])
                  .map((c) => ROTULO_ETAPA[c] || c)
                  .join(", ");
                return (
                  <tr
                    key={op.id}
                    data-clicavel={podeGerir ? "true" : undefined}
                    onClick={() => podeGerir && setForm({ operador: op })}
                    title={podeGerir ? "Editar colaborador" : op.nome}
                  >
                    <td title={op.nome}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <UserAvatar nome={op.nome} size="xs" />
                        {op.nome}
                      </span>
                    </td>
                    <td>{op.funcao_display || op.funcao}</td>
                    <td>
                      {op.nivel ? (
                        <Pill $tone={NIVEL_OPERADOR_TONES[op.nivel] || "neutral"}>
                          {op.nivel}
                        </Pill>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td>
                      {op.eh_executor_producao ? (
                        <Pill $tone="success">Sim</Pill>
                      ) : (
                        <Pill $tone="neutral">Não</Pill>
                      )}
                    </td>
                    <td title={comps || "sem competências registradas"}>
                      {comps || "–"}
                    </td>
                    <td>
                      {op.eh_supervisor ? <Pill $tone="info">Sim</Pill> : "–"}
                    </td>
                    <td>
                      {op.esta_bloqueado ? (
                        <Pill $tone="danger">Bloqueado</Pill>
                      ) : op.ativo ? (
                        <Pill $tone="success">Ativo</Pill>
                      ) : (
                        <Pill $tone="neutral">Inativo</Pill>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TabelaFixa>
        )}
        <NotaDiscreta>
          Quem aparece no board da Marcenaria: flag executor de produção +
          ativo no período. Desativar é soft-delete (preserva o histórico de
          bipagem).
        </NotaDiscreta>
      </Card>

      <FormOperador
        aberto={Boolean(form)}
        operador={form?.operador || null}
        onFechar={() => setForm(null)}
        onSalvo={carregar}
      />
    </>
  );
}
